import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { withWsToken } from '../services/wsAuth';
import {
  FiMessageSquare, FiPhone, FiPhoneIncoming, FiPhoneOff,
  FiCopy, FiCheck, FiX, FiBell, FiBellOff, FiVolume2,
  FiVolumeX, FiClock, FiAlertCircle
} from 'react-icons/fi';
import './RealtimeNotifications.css';

// WebSocket connection URL
// Same-origin by default: production proxies /ws from the main vhost.
const WS_URL = process.env.REACT_APP_WS_URL ||
  (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
  window.location.host + '/ws';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function RealtimeNotifications({ onClose }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [callbackConfig, setCallbackConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Track active audio resources for cleanup
  const activeAudioRef = useRef([]);

  // Play notification sound
  const playSound = useCallback((type) => {
    if (muted) return;
    
    try {
      // Create oscillator for notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Track for cleanup on unmount
      const audioEntry = { audioContext, oscillator, timers: [] };
      activeAudioRef.current.push(audioEntry);
      
      if (type === 'sms') {
        // Short beeps for SMS
        oscillator.frequency.value = 880;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        oscillator.start();
        const t1 = setTimeout(() => {
          oscillator.frequency.value = 1100;
        }, 100);
        const t2 = setTimeout(() => {
          oscillator.stop();
          audioContext.close();
          activeAudioRef.current = activeAudioRef.current.filter(e => e !== audioEntry);
        }, 200);
        audioEntry.timers.push(t1, t2);
      } else if (type === 'call') {
        // Ring tone for calls
        oscillator.frequency.value = 440;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.4;
        oscillator.start();
        
        let toggle = true;
        const ringInterval = setInterval(() => {
          oscillator.frequency.value = toggle ? 440 : 480;
          toggle = !toggle;
        }, 500);
        audioEntry.ringInterval = ringInterval;
        
        const t1 = setTimeout(() => {
          clearInterval(ringInterval);
          oscillator.stop();
          audioContext.close();
          activeAudioRef.current = activeAudioRef.current.filter(e => e !== audioEntry);
        }, 2000);
        audioEntry.timers.push(t1);
      }
    } catch (e) {
      console.log('Audio not available');
    }
  }, [muted]);

  // Track disposed state to prevent zombie reconnections
  const disposedRef = useRef(false);
  const retryCountRef = useRef(0);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (disposedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    console.log('[WS] Connecting to:', WS_URL);
    const ws = new WebSocket(withWsToken(WS_URL));

    ws.onopen = () => {
      console.log('[WS] Connected');
      setConnected(true);
      retryCountRef.current = 0;

      // Clear any reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[WS] Message:', message);

        if (message.event === 'sms_received') {
          const notification = {
            ...message.data,
            id: message.data.id || Date.now().toString(),
            receivedAt: new Date().toISOString()
          };
          setNotifications(prev => [notification, ...prev].slice(0, 50));
          playSound('sms');
          
          // Show browser notification if permitted
          showBrowserNotification('SMS Received', 
            message.data.otp 
              ? `OTP: ${message.data.otp} from ${message.data.service}`
              : `Message from ${message.data.from}`
          );
        }

        if (message.event === 'call_received') {
          const notification = {
            ...message.data,
            id: message.data.id || Date.now().toString(),
            receivedAt: new Date().toISOString()
          };
          setNotifications(prev => [notification, ...prev].slice(0, 50));
          playSound('call');
          
          showBrowserNotification('Incoming Call', 
            `Call from ${message.data.from} (${message.data.service})`
          );
        }
      } catch (e) {
        console.error('[WS] Parse error:', e);
      }
    };

    ws.onclose = () => {
      console.log('[WS] Disconnected');
      setConnected(false);
      
      // Only reconnect if not disposed, with exponential backoff
      if (!disposedRef.current) {
        const delay = Math.min(3000 * Math.pow(2, retryCountRef.current), 60000);
        retryCountRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log(`[WS] Attempting to reconnect (attempt ${retryCountRef.current})...`);
          connectWebSocket();
        }, delay);
      }
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };

    wsRef.current = ws;
  }, [playSound]);

  // Show browser notification
  const showBrowserNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'rfid-vault-notification'
      });
    }
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  // Connect on mount
  useEffect(() => {
    disposedRef.current = false;
    requestNotificationPermission();
    connectWebSocket();

    // Load webhook config (URLs/instructions) for easier provider setup
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/callbacks/config`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (disposedRef.current) return;
        setCallbackConfig(json);
        setConfigError(null);
      } catch (e) {
        if (disposedRef.current) return;
        setConfigError(e?.message || 'Failed to load callback config');
      }
    })();

    return () => {
      disposedRef.current = true;
      // Clean up all active audio resources
      activeAudioRef.current.forEach(entry => {
        entry.timers.forEach(t => clearTimeout(t));
        if (entry.ringInterval) clearInterval(entry.ringInterval);
        try { entry.oscillator.stop(); } catch (e) { /* already stopped */ }
        try { entry.audioContext.close(); } catch (e) { /* already closed */ }
      });
      activeAudioRef.current = [];
      // Clean up WebSocket
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent zombie reconnection
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  // Copy OTP to clipboard
  const copyOTP = async (otp, id) => {
    try {
      await navigator.clipboard.writeText(otp);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
  };

  // Format time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get status icon for calls
  const getCallStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'ringing':
        return <FiPhoneIncoming className="ringing" />;
      case 'answered':
      case 'in-progress':
        return <FiPhone className="active" />;
      case 'completed':
      case 'ended':
        return <FiPhoneOff className="ended" />;
      default:
        return <FiPhoneIncoming />;
    }
  };

  return (
    <div className="realtime-notifications">
      {/* Header */}
      <div className="notifications-header">
        <div className="header-left">
          <FiBell className={connected ? 'connected' : 'disconnected'} />
          <h3>{t('notifications.title', 'Real-time Notifications')}</h3>
          <span className={`status-badge ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? t('notifications.connected', 'Connected') : t('notifications.disconnected', 'Disconnected')}
          </span>
        </div>
        <div className="header-actions">
          <button 
            className={`btn-icon ${muted ? 'muted' : ''}`}
            onClick={() => setMuted(!muted)}
            title={muted ? t('notifications.unmute', 'Unmute') : t('notifications.mute', 'Mute')}
          >
            {muted ? <FiVolumeX /> : <FiVolume2 />}
          </button>
          <button 
            className="btn-icon"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? t('notifications.collapse', 'Collapse') : t('notifications.expand', 'Expand')}
          >
            {expanded ? <FiBellOff /> : <FiBell />}
          </button>
          {notifications.length > 0 && (
            <button 
              className="btn-clear"
              onClick={clearAll}
            >
              {t('notifications.clearAll', 'Clear All')}
            </button>
          )}
          <button className="btn-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
      </div>

      {/* Notification List */}
      {expanded && (
        <div className="notifications-list">
          {notifications.length === 0 ? (
            <div className="empty-state">
              <FiBell size={48} />
              <h4>{t('notifications.noNotifications', 'No Notifications')}</h4>
              <p>{t('notifications.waitingDesc', 'Waiting for incoming SMS or calls...')}</p>
              <div className="webhook-info">
                <FiAlertCircle />
                <span>{t('notifications.setupHint', 'Configure your provider webhooks to receive notifications')}</span>
              </div>

              {/* Webhook URLs helper */}
              {callbackConfig && (
                <div className="webhook-config" style={{ marginTop: 16, width: '100%', maxWidth: 520 }}>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 8 }}>
                    {t('notifications.webhookUrls', 'Webhook URLs')}:
                  </div>

                  {[
                    { label: 'SMS', value: callbackConfig.smsWebhook, id: 'cfg-sms' },
                    { label: 'CALL', value: callbackConfig.callWebhook, id: 'cfg-call' },
                    { label: 'TEST', value: callbackConfig.testWebhook, id: 'cfg-test' }
                  ].map((row) => (
                    <div key={row.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ width: 48, fontSize: 12, opacity: 0.8 }}>{row.label}</div>
                      <input
                        value={row.value || ''}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid rgba(255,255,255,0.12)',
                          background: 'rgba(0,0,0,0.18)',
                          color: 'rgba(255,255,255,0.9)'
                        }}
                      />
                      <button
                        className="btn-copy-otp"
                        onClick={() => copyText(row.value || '', row.id)}
                        title={t('common.copy', 'Copy')}
                        disabled={!row.value}
                      >
                        {copiedId === row.id ? <FiCheck /> : <FiCopy />}
                      </button>
                    </div>
                  ))}

                  {callbackConfig.webhookSecretRequired && (
                    <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
                      {callbackConfig.webhookSecretHowTo || t('notifications.webhookSecret', 'Webhook secret is required (append ?secret=...)')}
                    </div>
                  )}
                </div>
              )}

              {configError && (
                <div style={{ marginTop: 12, fontSize: 12, opacity: 0.75 }}>
                  {t('notifications.webhookConfigError', 'Could not load webhook config')}: {configError}
                </div>
              )}
            </div>
          ) : (
            notifications.map(notification => (
              <div 
                key={notification.id} 
                className={`notification-item ${notification.type} ${notification.isOTP ? 'has-otp' : ''}`}
              >
                <div className="notification-icon">
                  {notification.type === 'sms' && <FiMessageSquare />}
                  {notification.type === 'call' && getCallStatusIcon(notification.status)}
                </div>
                
                <div className="notification-content">
                  <div className="notification-header">
                    <span className="service-name">{notification.service}</span>
                    <span className="notification-time">
                      <FiClock size={12} />
                      {formatTime(notification.receivedAt)}
                    </span>
                  </div>
                  
                  <div className="notification-from">
                    {t('notifications.from', 'From')}: {notification.from}
                  </div>

                  {notification.type === 'sms' && (
                    <>
                      {notification.otp && (
                        <div className="otp-display">
                          <span className="otp-label">OTP:</span>
                          <span className="otp-code">{notification.otp}</span>
                          <button 
                            className="btn-copy-otp"
                            onClick={() => copyOTP(notification.otp, notification.id)}
                          >
                            {copiedId === notification.id ? <FiCheck /> : <FiCopy />}
                          </button>
                        </div>
                      )}
                      <div className="message-preview">
                        {notification.message}
                      </div>
                    </>
                  )}

                  {notification.type === 'call' && (
                    <div className="call-status">
                      <span className={`status ${notification.status?.toLowerCase()}`}>
                        {notification.status}
                      </span>
                      <span className="direction">
                        ({notification.direction})
                      </span>
                    </div>
                  )}
                </div>

                <button 
                  className="btn-dismiss"
                  onClick={() => removeNotification(notification.id)}
                >
                  <FiX />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Mini view when collapsed */}
      {!expanded && notifications.length > 0 && (
        <div className="notifications-mini">
          {notifications.slice(0, 3).map(n => (
            <div key={n.id} className={`mini-item ${n.type}`}>
              {n.type === 'sms' ? <FiMessageSquare /> : <FiPhone />}
              {n.otp && <span className="mini-otp">{n.otp}</span>}
            </div>
          ))}
          {notifications.length > 3 && (
            <span className="more-count">+{notifications.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default RealtimeNotifications;
